import React from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Home.css";
import servicesData from "./servicesData";

function Home() {
  const navigate = useNavigate();

  // Show only 6 services for a balanced grid
  const featuredServices = servicesData.slice(0, 6);

  return (
    <>
      {/* Hero Section */}
      <div className="hero">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline 
          className="hero-video"
        >
          <source src="/original/solar_4.mp4" type="video/mp4" />
        </video>
        <div className="overlay"></div>
        <div className="content">
          <h1>Professional Security Solutions Across the Midlands</h1>
          <h2>
            SIA Licensed Security Officers &amp; <span className="highlight">Expert Protection</span> for Your Business
          </h2>
          <p>
            Trusted security professional services for businesses across the Midlands. From construction site guarding to door supervisor services,
            we deliver comprehensive protection tailored to your needs.
          </p>
          <div className="buttons">
            <button className="btn primary" onClick={() => navigate("/contact")}>
              GET A FREE QUOTE
            </button>
            <button className="btn secondary" onClick={() => navigate("/services")}>
              VIEW ALL SERVICES
            </button>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section className="about-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">ABOUT US</span>
            <h2>Observant Security - Your Trusted Security Provider</h2>
            <p className="section-description">
              Observant Security Solution is a professional, service-driven security company
              dedicated to providing reliable solutions across the Midlands. As leading specialists in
              <span className="highlight-text"> Solar Panel Security</span> and renewable energy asset protection, 
              we focus on safeguarding your property, people, and premises with elite SIA licensed personnel.
            </p>
          </div>
          <div className="about-cards">
            <div className="about-card">
              <div className="card-icon">
                <img src="/Pages%20Images/CIA_Licensed.png" alt="SIA Licensed Security Officers" loading="lazy" />
              </div>
              <h3>SIA Licensed Security Officers</h3>
              <p>All our security personnel are fully SIA licensed and professionally trained to the highest standards.</p>
            </div>
            <div className="about-card">
              <div className="card-icon">
                <img src="/Pages%20Images/tailored_Sol.png" alt="Integrated Security Solutions" loading="lazy" />
              </div>
              <h3>Integrated Security Solutions</h3>
              <p>Specialist static guarding for comprehensive site protection across the Midlands.</p>
            </div>
            <div className="about-card">
              <div className="card-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64" aria-hidden="true" focusable="false">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#c8a951" strokeWidth="4"/>
                  <line x1="32" y1="16" x2="32" y2="32" stroke="#c8a951" strokeWidth="4" strokeLinecap="round"/>
                  <line x1="32" y1="32" x2="44" y2="40" stroke="#c8a951" strokeWidth="4" strokeLinecap="round"/>
                  <circle cx="32" cy="32" r="3" fill="#c8a951"/>
                </svg>
              </div>
              <h3>Reliable Availability</h3>
              <p>Consistent security services with rapid response times across the Midlands.</p>
            </div>
            <div className="about-card">
              <div className="card-icon">
                <img src="/Pages%20Images/ExperiencedTeam.png" alt="Fast-Growing Company" loading="lazy" />
              </div>
              <h3>Fast-Growing Company</h3>
              <p>Committed to excellence in providing tailored security solutions to diverse industries across the Midlands.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Showcase */}
      <section className="services-showcase">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">OUR SERVICES</span>
            <h2>Comprehensive Security Solutions</h2>
            <p className="section-description">
              We deliver a wide range of security solutions to suit the needs of your business.
              Our guards have protected construction sites, building sites, retail premises, and many more.
            </p>
          </div>
          <div className="services-grid">
            {featuredServices.map((service, index) => (
              <Link to={`/service/${index}`} key={index} className="service-card">
                <div className="service-image">
                  <img src={service.image} alt={service.title} loading="lazy" />
                  <div className="service-overlay"></div>
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <span className="service-arrow">Learn More →</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="view-all-services buttons">
            <button className="btn primary" onClick={() => navigate("/services")}>
              VIEW ALL SERVICES
            </button>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="statistics-section">
        <div className="section-container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">MIDLANDS</div>
              <div className="stat-label">Operations across the Midlands</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">100%</div>
              <div className="stat-label">Client Satisfaction Guaranteed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">RAPID</div>
              <div className="stat-label">Response Protection</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">LICENSED</div>
              <div className="stat-label">SIA Licensed Security Officers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="industries-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">INDUSTRIES WE SERVE</span>
            <h2>Protecting Businesses Across All Sectors</h2>
          </div>
          <div className="industries-grid">
            <div className="industry-card">
              <div className="industry-icon">
                <img src="/Pages%20Images/Construction_icon_Home_page.png" alt="Construction Site" loading="lazy" decoding="async" />
              </div>
              <h3>Construction Site</h3>
            </div>
            <div className="industry-card">
              <div className="industry-icon">
                <img src="/Pages%20Images/Warehouse_icon_Home_page.png" alt="Building Site" loading="lazy" decoding="async" />
              </div>
              <h3>Building Site</h3>
            </div>
            <div className="industry-card">
              <div className="industry-icon">
                <img src="/Services/24.solarfarms.png" alt="Solar Panel" loading="lazy" decoding="async" />
              </div>
              <h3>Solar Panel</h3>
            </div>
            <div className="industry-card">
              <div className="industry-icon">
                <img src="/Pages%20Images/Retaild_icon_Home_page.png" alt="Retail Security" loading="lazy" decoding="async" />
              </div>
              <h3>Retail Security</h3>
            </div>
            <div className="industry-card">
              <div className="industry-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80" aria-hidden="true">
                  <rect x="18" y="8" width="44" height="64" rx="4" fill="#e8e0cc" stroke="#c8a951" strokeWidth="2.5"/>
                  <rect x="22" y="12" width="36" height="56" rx="2" fill="#f5f0e8"/>
                  <rect x="22" y="12" width="18" height="56" rx="2" fill="#ede5d0"/>
                  <line x1="40" y1="12" x2="40" y2="68" stroke="#c8a951" strokeWidth="1.5"/>
                  <circle cx="46" cy="40" r="3" fill="#c8a951"/>
                  <rect x="8" y="10" width="6" height="60" rx="2" fill="#b8a070"/>
                </svg>
              </div>
              <h3>Door Supervisor</h3>
            </div>
            <div className="industry-card">
              <div className="industry-icon">
                <img src="/Pages%20Images/Corporate_icon_Home_page.png" alt="Corporate" loading="lazy" decoding="async" />
              </div>
              <h3>Corporate</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditations — floating marquee, approved logos only */}
      <section className="contractors-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">ACCREDITATIONS</span>
            <h2>Accreditations &amp; Certifications</h2>
          </div>
          <div className="contractors-marquee">
            <div className="contractors-track">
              {[...Array(2)].map((_, i) => (
                <React.Fragment key={i}>
                  <a
                    href="https://ico.org.uk/ESDWebPages/Entry/ZB538133"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contractor-logo"
                    aria-label="ICO Registered - Information Commissioner's Office"
                  >
                    <img src="/license images/contracters_logo_2.png" alt="ICO Registered" loading="lazy" decoding="async" />
                  </a>
                  <a
                    href="https://www.alcumus.com/safecontractor"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contractor-logo"
                    aria-label="SafeContractor Approved"
                  >
                    <img src="/license images/contracters_logo_3.png" alt="SafeContractor Approved" loading="lazy" decoding="async" />
                  </a>
                  <a
                    href="https://www.constructionline.co.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contractor-logo"
                    aria-label="Constructionline Gold Member"
                  >
                    <img src="/license images/contracters_logo_4.png" alt="Constructionline Gold Member" loading="lazy" decoding="async" />
                  </a>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Ready to Secure Your Business?</h2>
            <p>Contact us today for a free, no-obligation security consultation</p>
            <div className="cta-buttons">
              <button className="btn primary large" onClick={() => navigate("/contact")}>
                GET A FREE QUOTE
              </button>
              <a href="tel:+447446084868" className="btn secondary large">
                📞 Call +44 7446 084868
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
